import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { useAuth } from '@/hooks/useAuth'
import { useDashboard } from '../useDashboard'

/**
 * Dashboard tile showing the caller's recent request events.
 *
 * Reads from `useDashboard()`'s shared cache — no own fetch, no own
 * staleTime. Layout matches the rest of the dashboard widget shell
 * (heading + content), with explicit loading / empty / error states
 * scoped to this card so a 500 here does not affect siblings.
 *
 * Spec 4 / Group 9d. Per design.md Open Question 1, this widget
 * intentionally surfaces ALL events the recent-activity endpoint
 * returns — including comment events scoped via the request-center
 * envelope. Each row renders a sentence-cased verb and the entity it
 * refers to (request number for request events, "you" for self-userId
 * on auth/user events) so the human reading it doesn't need to decode
 * raw UUIDs.
 */
interface ActivityEvent {
	eventId: string
	entity?: string
	action: string
	timestamp: string
	userId?: string
	entityType?: string
	entityId?: string
	data?: Record<string, unknown>
}

interface RecentActivityPayload {
	data: ActivityEvent[]
}

export function RecentActivityWidget() {
	const { user } = useAuth()
	const { recentActivityQuery } = useDashboard()

	if (recentActivityQuery.isError) {
		return (
			<WidgetCard title="Your recent and relevant activity">
				<p className="text-sm text-red-700">Couldn't load recent activity. Try refreshing the page.</p>
			</WidgetCard>
		)
	}

	if (recentActivityQuery.isLoading || recentActivityQuery.isPending) {
		return (
			<WidgetCard title="Your recent and relevant activity">
				<p className="text-sm text-gray-500">Loading…</p>
			</WidgetCard>
		)
	}

	const payload = recentActivityQuery.data as RecentActivityPayload | undefined
	const events = payload?.data ?? []

	if (events.length === 0) {
		return (
			<WidgetCard title="Your recent and relevant activity">
				<p className="text-sm text-gray-500">No recent activity yet.</p>
			</WidgetCard>
		)
	}

	return (
		<WidgetCard title="Your recent and relevant activity">
			<ul className="divide-y divide-gray-100" aria-label="Recent activity list">
				{events.map((ev) => (
					<EventRow key={ev.eventId} event={ev} viewerUserId={user?.userId} />
				))}
			</ul>
		</WidgetCard>
	)
}

interface RequestRef {
	label: string
	href?: string
}

function EventRow({ event, viewerUserId }: { event: ActivityEvent; viewerUserId: string | undefined }) {
	const verb = humaniseEvent(event)
	const ref = describeTarget(event, viewerUserId)

	return (
		<li className="flex items-baseline justify-between gap-3 px-2 py-2">
			<span className="text-sm text-gray-700">
				<span className="font-medium">{verb}</span>
				{ref ? <TargetLabel ref={ref} /> : null}
			</span>
			<span className="text-xs text-gray-400">{new Date(event.timestamp).toLocaleString()}</span>
		</li>
	)
}

function TargetLabel({ ref }: { ref: RequestRef }) {
	if (ref.href) {
		return (
			<>
				{' '}
				<Link to={ref.href} className="text-gray-500 hover:underline">
					{ref.label}
				</Link>
			</>
		)
	}
	return <span className="text-gray-500"> {ref.label}</span>
}

/**
 * "Request submitted", "Request state changed", "Comment created", "Sync
 * requested", etc. Combines `entity` (when present and not redundant) with
 * `action` (`_` → space, sentence-cased).
 *
 * Some entity prefixes are dropped because they read awkwardly in plain
 * English ("user logged in" → "Logged in"); the rest stay so the reader
 * has context ("Request submitted" beats "Submitted" alone).
 */
function humaniseEvent(event: ActivityEvent): string {
	const action = humanisePhrase(event.action)
	const entity = event.entity?.toLowerCase()
	if (!entity || ENTITY_PREFIXES_TO_DROP.has(entity)) return action
	const entityWord = humanisePhrase(entity)
	return `${entityWord} ${action.toLowerCase()}`
}

const ENTITY_PREFIXES_TO_DROP = new Set(['user', 'auth', 'session'])

function humanisePhrase(s: string): string {
	if (!s) return ''
	const spaced = s.replace(/_/g, ' ')
	return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * Resolve a human label for the row's target. Priority:
 *   1. `data.requestNumber` — every request-center event carries it; renders
 *      as a link to the request detail page when `entityId` is present.
 *   2. Self-userId on the row → `you` (e.g. "Logged in — you").
 *   3. Fallback: drop the trailing UUID rather than truncate it. UUIDs
 *      aren't human-meaningful, so an empty trailing label is preferable
 *      to "on 196bf2ed…".
 */
function describeTarget(event: ActivityEvent, viewerUserId: string | undefined): RequestRef | null {
	const requestNumber = typeof event.data?.requestNumber === 'string' ? event.data.requestNumber : undefined
	if (requestNumber) {
		return {
			label: `— ${requestNumber}`,
			href: event.entityId ? `/requests-center/${event.entityId}` : undefined,
		}
	}
	if (viewerUserId && event.userId === viewerUserId) {
		return { label: '— you' }
	}
	return null
}

function WidgetCard({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<Card className="p-4">
			<h2 className="mb-3 text-sm font-semibold text-gray-700">{title}</h2>
			{children}
		</Card>
	)
}
